import {
  forwardRef,
  RefObject,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import RenderNoRecords from '../../../../Atoms/NoRecords/NoRecords';
import intl from 'react-intl-universal';
import emptyDashboardIcon from '../../../../assets/images/empty-dashboard.svg';
import { Typography } from '@getsynapse/design-system';
import { DashboardContext } from '../../DashboardContext';
import RGL, { Layout, WidthProvider } from 'react-grid-layout';
import {
  getCardTypes,
  cardTypeSelectors,
} from '../../../../state/normalized/insights/cardTypeSlice';
import { RootState, useAppThunkDispatch } from '../../../../state/store';
import { Card } from '../../../../utils/customTypes';
import { useSelector } from 'react-redux';
import {
  cardSelectors,
  updateCardLayoutLocal,
} from '../../../../state/normalized/dashboard/cardSlice';
import { GridItem } from './GridItem';
import { SLICE_STATUS } from '../../../../utils/constants';
import { dashboardSelectors } from '../../../../state/normalized/dashboard/dashboardSlice';
import { AddInsightsButton } from '../AddInsightsButton';
import useResizeObserver from 'Hooks/useResizeObserver';
import { getInsightChartData } from 'state/normalized/insights/chartDataSlice';
import { dashboardFiltersSelectors } from 'state/normalized/dashboard/dashboardFiltersSlice';
import { useCardsDataDependency } from '../../hooks/useCardsDataDependency';

export interface InsightsGridProps {}

export interface InsightsGridRef {
  getGridRef: () => RefObject<HTMLDivElement> | null;
}

// Grid layout constants
const DEFAULT_ROW_HEIGHT = 200;
const GRID_COLUMN_COUNT = 3;
const GRID_MARGIN: [number, number] = [16, 16];

const NoCardsView = () => (
  <RenderNoRecords
    className='h-full'
    caption={intl.get('DASHBOARD_DETAIL_PAGE.EMPTY_CAPTION')}
    imageClassName='hidden'
    labelClassName='mt-1 text-xl py-2'
  >
    <div className='flex justify-center items-stretch gap-x-6'>
      <div className='shadow-skim p-6 rounded-lg flex flex-col justify-center h-full'>
        <img src={emptyDashboardIcon} alt='Empty Dashboard' className='h-34' />
        <Typography className='text-center mt-2'>
          {intl.getHTML('DASHBOARD_DETAIL_PAGE.EMPTY_SUB_CAPTION_1')}
        </Typography>
        <AddInsightsButton style={`body`} />
      </div>
    </div>
  </RenderNoRecords>
);

const InsightsGrid = forwardRef<InsightsGridRef, InsightsGridProps>(
  (_, ref) => {
    const dispatch = useAppThunkDispatch();
    const { dashboardId } = useContext(DashboardContext);
    const gridRef = useRef(null);

    const cardTypes = useSelector(cardTypeSelectors.selectEntities);

    const { width: gridWidth } = useResizeObserver(gridRef);

    const selectCardsForThisDashboard = useMemo(
      () => cardSelectors.selectWorkingCardsForDashboard(dashboardId),
      [dashboardId]
    );
    const cards = useSelector(selectCardsForThisDashboard);
    const dashboardStatus = useSelector((rootState: RootState) =>
      dashboardSelectors.selectDashboardStatusByDashboardId(
        rootState,
        dashboardId
      )
    );

    const { canEdit } = useSelector((rootState: RootState) =>
      dashboardSelectors.selectDashboardAffordanceFlags(rootState, dashboardId)
    );

    const filters = useSelector((state: RootState) =>
      dashboardFiltersSelectors.selectAppliedFiltersForRequestById(
        state,
        dashboardId
      )
    );

    const cardsLoading =
      dashboardStatus === SLICE_STATUS.LOADING ||
      dashboardStatus === SLICE_STATUS.UPDATING;

    const chartsRef = useRef(null);

    const { cardsSignature, filtersSignature } = useCardsDataDependency(
      dashboardId,
      cards,
      filters
    );

    useImperativeHandle(ref, () => ({
      getGridRef: () => {
        return chartsRef;
      },
    }));

    const fetchCardsData = useCallback(() => {
      if (!cards || !cardTypes) return;

      cards.forEach((card) => {
        const cardType = cardTypes[card.cardTypeId];

        if (cardType) {
          dispatch(
            getInsightChartData({
              dataUrl: cardType.dataUrl,
              filters,
            })
          );
        }
      });
    }, [cards, filters, cardTypes, dispatch]);

    useEffect(() => {
      fetchCardsData();
      // this rule is disabled here to have more control over the data fetching dependencies
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cardsSignature, filtersSignature]);

    // TODO: This guarantees a single call to getCardTypes, but has the
    //  possibility of never updating the list of card types.
    // TODO: Investigate always making the call, but using HTTP itself and the
    //  browser cache.
    useEffect(() => {
      dispatch(getCardTypes());
    }, [dispatch]);

    const layout: Layout[] = cards.map(
      (card): Layout => ({
        // TODO: If neither Id exists, we are in trouble and need to fix this.
        i: card.id ?? card.lid ?? '',
        x: card.x,
        y: card.y,
        w: card.width,
        h: card.height,
      })
    );

    // TODO: The WidthProvider should be memoized for performance reasons: https://github.com/react-grid-layout/react-grid-layout?tab=readme-ov-file#react-hooks-performance
    const DashboardGrid = useMemo(() => WidthProvider(RGL), []);

    const gridComponents = useMemo(
      () =>
        cards.map((card: Card) => (
          // TODO: If neither Id exists, we are in trouble and need to fix this.
          <GridItem
            key={card.id ?? card.lid ?? ''}
            card={card}
            canEdit={canEdit}
          />
        )),
      [cards, canEdit]
    );

    return (
      <>
        {!cardsLoading && cards?.length === 0 && <NoCardsView />}
        {!cardsLoading && Array.isArray(layout) && layout.length > 0 && (
          <div className='relative' ref={gridRef} key={gridWidth}>
            <DashboardGrid
              innerRef={chartsRef}
              className='-ml-4'
              margin={GRID_MARGIN}
              cols={GRID_COLUMN_COUNT}
              width={gridWidth + 32}
              rowHeight={DEFAULT_ROW_HEIGHT}
              draggableHandle='.drag-handle'
              compactType='vertical'
              layout={layout}
              measureBeforeMount
              isDraggable={canEdit}
              isResizable={canEdit}
              onDragStop={(nextLayout) => {
                dispatch(
                  updateCardLayoutLocal({
                    dashboardId,
                    layout: nextLayout,
                  })
                );
              }}
              onResizeStop={(nextLayout) => {
                dispatch(
                  updateCardLayoutLocal({
                    dashboardId,
                    layout: nextLayout,
                  })
                );
              }}
            >
              {gridComponents}
            </DashboardGrid>
          </div>
        )}
      </>
    );
  }
);

export default InsightsGrid;
